import React, { useState, useRef } from 'react';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import { useParams } from 'react-router-dom';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { toPng } from 'html-to-image';
import { AddIcon, KeyboardArrowDownIcon } from 'icons/materialIcons';
import SignatureContent from './SignatureContent';
import AddToEmailSignatureModal from 'components/emailSignature/AddToEmailSignatureModal';
import { copyEmailSignatureHTML } from 'utils/emailSignature/copyEmailSignature';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';

import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Menu,
  MenuItem
} from '@mui/material';

const EmailSignaturePreview = () => {
  const { cardId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const signatureRef = useRef(null);
  const { emailSignatureState } = useEmailSignatureContext();

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };

  const handleDownloadImage = async () => {
    if (signatureRef.current) {
      setDownloading(true);
      handleClose();

      try {
        const scale = Math.min(
          1350 / signatureRef.current.offsetWidth,
          1428 / signatureRef.current.offsetHeight
        );

        const dataUrl = await toPng(signatureRef.current, {
          width: 1350,
          height: 1428,
          style: {
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: `${signatureRef.current.offsetWidth}px`,
            height: `${signatureRef.current.offsetHeight}px`,
          }
        });

        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `email-signature-${cardId}.png`;
        link.click();
      } catch (err) {
        console.error('Could not generate image:', err);
        setError('Failed to download signature image');
      } finally {
        setDownloading(false);
      }
    }
  };

  const handleCopyHTML = async () => {
    setDownloading(true);
    handleClose();

    try {
      if (!emailSignatureState[cardId]) {
        throw new Error('Email signature state not found');
      }

      await copyEmailSignatureHTML({
        state: emailSignatureState[cardId],
        contentRef: signatureRef,
        cardUrl: `${FRONTEND_BASE_URL}/card/${cardId}`
      });
      setSuccessSnackbarOpen(true);
    } catch (error) {
      console.error('Copy failed:', error);
      alert(`Failed to copy HTML code: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenAddToEmailSignatureModal = () => setModalOpen(true);
  const handleCloseAddToEmailSignatureModal = () => setModalOpen(false);
  const handleDownloadClick = (event) => !downloading && setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <Box>
      <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: "#828282", mb: "20px", mt: "30px", textAlign: 'center' }}>
        Signature Preview
      </Typography>

      <Box sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
        <SignatureContent
          ref={signatureRef}
          emailSignatureState={emailSignatureState}
          cardId={cardId}
          setLoading={setLoading}
          setError={setError}
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddToEmailSignatureModal}
          sx={{
            backgroundColor: 'black',
            color: 'white',
            fontSize: '0.6rem',
            fontWeight: '600',
            borderRadius: '50px',
            padding: '10px 30px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'black',
            },
          }}
        >
          Add to your email
        </Button>

        <Button
          onClick={handleDownloadClick}
          endIcon={downloading ? null : <KeyboardArrowDownIcon />}
          disabled={downloading}
          sx={{
            color: '#2196f3',
            textTransform: 'none',
            background: 'transparent',
            '&:hover': {
              background: 'transparent',
            },
            minWidth: '100px',
            padding: '6px 8px',
          }}
        >
          {downloading ? (
            <>
              <CircularProgress size={16} sx={{ color: '#2196f3', marginRight: 1 }} />
              Downloading...
            </>
          ) : (
            'Download'
          )}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && !downloading}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem onClick={handleDownloadImage} disabled={downloading}>
            Download Signature Image
          </MenuItem>
          <MenuItem onClick={handleCopyHTML} disabled={downloading}>
            Copy Signature HTML
          </MenuItem>
        </Menu>

        <AddToEmailSignatureModal
          open={modalOpen}
          onClose={handleCloseAddToEmailSignatureModal}
          contentRef={signatureRef}
        />
      </Box>

      <NotificationSnackbar
        open={successSnackbarOpen}
        severity="info"
        message="Email signature copied to clipboard."
        handleClose={handleCloseSuccessSnackbar}
        sx={{ zIndex: 1301 }}
      />
    </Box>
  );
};

export default EmailSignaturePreview;